import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [state, setState] = useState('loading'); // loading | done | error | resubscribed
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState('error'); return; }
    api.post('/marketing/unsubscribe', { token })
      .then((r) => { setEmail(r.data.email || ''); setState('done'); })
      .catch(() => setState('error'));
  }, [token]);

  const resubscribe = async () => {
    setBusy(true);
    try {
      const r = await api.post('/marketing/resubscribe', { token });
      setEmail(r.data.email || email);
      setState('resubscribed');
    } catch { /* noop */ }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-zinc-50" data-testid="unsubscribe-page">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-zinc-100 p-8 text-center">
        <div className="inline-flex w-12 h-12 rounded-xl bg-orange-500 text-white items-center justify-center mb-4">
          <Mail className="w-6 h-6" />
        </div>
        <div className="font-display font-black text-xl mb-1">PTT NZ Alerts</div>

        {state === 'loading' && (
          <div className="py-6 text-zinc-500 flex flex-col items-center gap-3" data-testid="unsub-loading">
            <Loader2 className="w-6 h-6 animate-spin" /> Processing your request…
          </div>
        )}

        {state === 'done' && (
          <div data-testid="unsub-done">
            <CheckCircle2 className="w-10 h-10 text-teal-500 mx-auto my-4" />
            <h1 className="font-bold text-lg mb-2">You've been unsubscribed</h1>
            <p className="text-sm text-zinc-600 mb-6">
              {email && <span className="font-medium">{email}</span>} will no longer receive product news and
              newsletters. You'll still get your road-safety alerts as normal.
            </p>
            <button onClick={resubscribe} disabled={busy} className="btn btn-ghost" data-testid="unsub-resubscribe">
              {busy ? 'Working…' : 'Changed your mind? Re-subscribe'}
            </button>
          </div>
        )}

        {state === 'resubscribed' && (
          <div data-testid="unsub-resubscribed">
            <CheckCircle2 className="w-10 h-10 text-teal-500 mx-auto my-4" />
            <h1 className="font-bold text-lg mb-2">You're re-subscribed</h1>
            <p className="text-sm text-zinc-600 mb-6">
              {email && <span className="font-medium">{email}</span>} will receive product news and newsletters again.
            </p>
            <Link to="/" className="btn btn-primary" data-testid="unsub-home">Back to site</Link>
          </div>
        )}

        {state === 'error' && (
          <div data-testid="unsub-error">
            <XCircle className="w-10 h-10 text-red-500 mx-auto my-4" />
            <h1 className="font-bold text-lg mb-2">This link isn't valid</h1>
            <p className="text-sm text-zinc-600 mb-6">
              The unsubscribe link is invalid or has been tampered with. You can manage email preferences from your
              account settings instead.
            </p>
            <Link to="/settings" className="btn btn-primary" data-testid="unsub-settings">Open settings</Link>
          </div>
        )}
      </div>
    </div>
  );
}
